import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getApiV1ExamExamidLangsAvailableQueryKey,
  getApiV1ExamExamidLangsQueryKey,
  usePostApiV1ExamExamidLangs,
} from '@/shared/api/generated';

/**
 * Hook para adicionar um idioma a uma prova via POST /api/v1/exam/{examId}/langs.
 *
 * Invalida a query de idiomas da prova após sucesso.
 *
 * @param examId - ID da prova à qual o idioma será adicionado
 * @returns Handler de submit e estado de loading
 *
 * @example
 * ```tsx
 * const { addLang, isLoading } = useAddExamLang(examId);
 * addLang({ cultureInfo: 'en-US', instruction: '...', active: true });
 * ```
 */
export function useAddExamLang(examId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examManagement');

  const mutation = usePostApiV1ExamExamidLangs({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamExamidLangsQueryKey(examId),
        });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamExamidLangsAvailableQueryKey(examId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  const addLang = (
    data: { cultureInfo: string; instruction?: string | null; active: boolean },
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutation.mutate(
      { examId, data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { addLang, isLoading: mutation.isPending } as const;
}
